import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ErrorBanner, FormField } from '@/components';
import EurodimaLogo from '@/components/assets/EurodimaLogo';
import { authApi, isAuthError } from '@/api';

function ResetPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestSent, setRequestSent] = useState(false);

  const canSubmitEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleRequestReset = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmitEmail) return;

    setIsLoading(true);
    setError(null);

    try {
      await authApi.requestPasswordReset({ email: email.trim().toLowerCase() });
      setRequestSent(true);
    } catch (err) {
      if (isAuthError(err)) {
        setError(err.message);
      } else {
        setError(t('auth.requestResetError', 'Failed to send reset email. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-background">
      <Card>
        <div className="flex justify-center mb-8">
          <div className="w-48">
            <EurodimaLogo />
          </div>
        </div>

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-onsurface">
            {t('auth.resetPassword', 'Reset Password')}
          </h1>
          <p className="mt-2 text-sm text-muted">
            {t('auth.requestResetSubtitle', 'Enter your email to receive a password reset link')}
          </p>
        </div>

        {requestSent ? (
          <div className="text-center">
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
              {t('auth.resetEmailSent', 'If an account exists with this email, you will receive a password reset link.')}
            </div>
            <Link
              to="/login"
              className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {t('auth.backToLogin', 'Back to login')}
            </Link>
          </div>
        ) : (
          <>
            <ErrorBanner hasError={!!error}>
              {error}
            </ErrorBanner>

            <form onSubmit={handleRequestReset} className="space-y-4">
              <FormField
                label={t('auth.email', 'Email')}
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />

              <button
                type="submit"
                disabled={isLoading || !canSubmitEmail}
                className="w-full py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-primary text-onprimary hover:bg-primary-darker focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                {isLoading ? (
                  <span className="animate-spin inline-block size-4 border-2 border-current border-t-transparent rounded-full" />
                ) : null}
                {t('auth.sendResetLink', 'Send Reset Link')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
              >
                {t('auth.backToLogin', 'Back to login')}
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/_auth/reset-password')({
  component: ResetPasswordPage,
});
